import { supabase } from './supabase';

export interface Profile {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface PortfolioSettings {
  id: string;
  user_id: string;
  initial_cash: number;
  current_cash: number;
  created_at: string;
  updated_at: string;
}

export interface Position {
  id: string;
  user_id: string;
  ticker: string;
  entry_date: string;
  entry_price: number;
  shares: number;
  exit_date: string | null;
  exit_price: number | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface PerformanceSnapshot {
  id: string;
  user_id: string;
  date: string;
  portfolio_value: number;
  cash_balance: number;
  created_at: string;
}

export interface PortfolioSummary {
  currentValue: number;
  initialValue: number;
  cashBalance: number;
  totalReturn: number;
  totalReturnPercentage: number;
  activePositions: number;
}

// Portfolio Management Service
export const portfolioService = {
  // Profile methods
  profile: {
    // Get the current user's profile
    getProfile: async (): Promise<Profile | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (error) throw error;
        
        return data as Profile;
      } catch (error) {
        console.error('Error getting profile:', error);
        return null;
      }
    },
    
    // Update the current user's profile
    updateProfile: async (profile: Partial<Profile>): Promise<Profile | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        const { data, error } = await supabase
          .from('profiles')
          .update(profile)
          .eq('id', user.id)
          .select()
          .single();
          
        if (error) throw error;
        
        return data as Profile;
      } catch (error) {
        console.error('Error updating profile:', error);
        return null;
      }
    },
    
    // Create a profile if it doesn't exist yet
    createProfileIfNeeded: async (): Promise<Profile | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        // Check if profile exists
        const { data: existingProfile, error: fetchError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();
          
        if (fetchError) throw fetchError;
        
        // If profile exists, return it
        if (existingProfile) return existingProfile as Profile;
        
        // Create new profile
        const { data, error } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            email: user.email,
            display_name: user.user_metadata?.full_name || null,
            avatar_url: user.user_metadata?.avatar_url || null,
          })
          .select()
          .single();
          
        if (error) throw error;
        
        return data as Profile;
      } catch (error) {
        console.error('Error creating profile:', error);
        return null;
      }
    },
  },
  
  // Portfolio Settings methods
  settings: {
    // Get portfolio settings for current user
    getSettings: async (): Promise<PortfolioSettings | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        const { data, error } = await supabase
          .from('portfolio_settings')
          .select('*')
          .eq('user_id', user.id)
          .single();
          
        if (error) throw error;
        
        return data as PortfolioSettings;
      } catch (error) {
        console.error('Error getting portfolio settings:', error);
        return null;
      }
    },
    
    // Update portfolio settings
    updateSettings: async (settings: Partial<PortfolioSettings>): Promise<PortfolioSettings | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        const { data, error } = await supabase
          .from('portfolio_settings')
          .update(settings)
          .eq('user_id', user.id)
          .select()
          .single();
          
        if (error) throw error;
        
        return data as PortfolioSettings;
      } catch (error) {
        console.error('Error updating portfolio settings:', error);
        return null;
      }
    },
    
    // Reset portfolio (set current cash to initial cash)
    resetPortfolio: async (): Promise<PortfolioSettings | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        // Get current settings
        const { data: settings, error: settingsError } = await supabase
          .from('portfolio_settings')
          .select('initial_cash')
          .eq('user_id', user.id)
          .single();
          
        if (settingsError) throw settingsError;
        
        // Update current cash to match initial cash
        const { data, error } = await supabase
          .from('portfolio_settings')
          .update({ current_cash: settings.initial_cash })
          .eq('user_id', user.id)
          .select()
          .single();
          
        if (error) throw error;
        
        // Also close any active positions
        await supabase
          .from('positions')
          .update({ is_active: false })
          .eq('user_id', user.id)
          .eq('is_active', true);
          
        return data as PortfolioSettings;
      } catch (error) {
        console.error('Error resetting portfolio:', error);
        return null;
      }
    },
  },
  
  // Positions methods
  positions: {
    // Get all positions for current user
    getAllPositions: async (): Promise<Position[]> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return [];
        
        const { data, error } = await supabase
          .from('positions')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        return data as Position[];
      } catch (error) {
        console.error('Error getting positions:', error);
        return [];
      }
    },
    
    // Get active positions for current user
    getActivePositions: async (): Promise<Position[]> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return [];
        
        const { data, error } = await supabase
          .from('positions')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        return data as Position[];
      } catch (error) {
        console.error('Error getting active positions:', error);
        return [];
      }
    },
    
    // Create a new position (buy)
    createPosition: async (
      ticker: string,
      shares: number,
      price: number,
      notes?: string
    ): Promise<Position | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        // Get current portfolio settings
        const { data: settings, error: settingsError } = await supabase
          .from('portfolio_settings')
          .select('current_cash')
          .eq('user_id', user.id)
          .single();
          
        if (settingsError) throw settingsError;
        
        // Calculate cost
        const cost = shares * price;
        
        // Check if user has enough cash
        if (settings.current_cash < cost) {
          throw new Error('Insufficient funds');
        }
        
        // Start a transaction
        // Create position
        const { data: position, error: positionError } = await supabase
          .from('positions')
          .insert({
            user_id: user.id,
            ticker,
            entry_date: new Date().toISOString().split('T')[0], // YYYY-MM-DD
            entry_price: price,
            shares,
            is_active: true,
            notes: notes || null,
          })
          .select()
          .single();
          
        if (positionError) throw positionError;
        
        // Update cash balance
        const { error: updateError } = await supabase
          .from('portfolio_settings')
          .update({
            current_cash: settings.current_cash - cost,
          })
          .eq('user_id', user.id);
          
        if (updateError) throw updateError;
        
        return position as Position;
      } catch (error) {
        console.error('Error creating position:', error);
        return null;
      }
    },
    
    // Close a position (sell)
    closePosition: async (
      positionId: string,
      exitPrice: number
    ): Promise<Position | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        // Get the position
        const { data: position, error: positionError } = await supabase
          .from('positions')
          .select('*')
          .eq('id', positionId)
          .eq('user_id', user.id)
          .single();
          
        if (positionError) throw positionError;
        
        // Calculate proceeds
        const proceeds = position.shares * exitPrice;
        
        // Get current portfolio settings
        const { data: settings, error: settingsError } = await supabase
          .from('portfolio_settings')
          .select('current_cash')
          .eq('user_id', user.id)
          .single();
          
        if (settingsError) throw settingsError;
        
        // Update position
        const { data: updatedPosition, error: updatePositionError } = await supabase
          .from('positions')
          .update({
            exit_date: new Date().toISOString().split('T')[0], // YYYY-MM-DD
            exit_price: exitPrice,
            is_active: false,
          })
          .eq('id', positionId)
          .eq('user_id', user.id)
          .select()
          .single();
          
        if (updatePositionError) throw updatePositionError;
        
        // Update cash balance
        const { error: updateSettingsError } = await supabase
          .from('portfolio_settings')
          .update({
            current_cash: settings.current_cash + proceeds,
          })
          .eq('user_id', user.id);
          
        if (updateSettingsError) throw updateSettingsError;
        
        return updatedPosition as Position;
      } catch (error) {
        console.error('Error closing position:', error);
        return null;
      }
    },
  },
  
  // Performance snapshot methods
  performance: {
    // Get performance snapshots for current user
    getSnapshots: async (limit = 30): Promise<PerformanceSnapshot[]> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return [];
        
        const { data, error } = await supabase
          .from('performance_snapshots')
          .select('*')
          .eq('user_id', user.id)
          .order('date', { ascending: false })
          .limit(limit);
          
        if (error) throw error;
        
        return data as PerformanceSnapshot[];
      } catch (error) {
        console.error('Error getting performance snapshots:', error);
        return [];
      }
    },
    
    // Create a performance snapshot
    createSnapshot: async (portfolioValue: number): Promise<PerformanceSnapshot | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        // Get current cash balance
        const { data: settings, error: settingsError } = await supabase
          .from('portfolio_settings')
          .select('current_cash')
          .eq('user_id', user.id)
          .single();
          
        if (settingsError) throw settingsError;
        
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        
        // Create snapshot
        const { data, error } = await supabase
          .from('performance_snapshots')
          .insert({
            user_id: user.id,
            date: today,
            portfolio_value: portfolioValue,
            cash_balance: settings.current_cash,
          })
          .select()
          .single();
          
        if (error) throw error;
        
        return data as PerformanceSnapshot;
      } catch (error) {
        console.error('Error creating performance snapshot:', error);
        return null;
      }
    },
  },
  
  // Get portfolio summary
  getPortfolioSummary: async (): Promise<PortfolioSummary | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return null;
      
      // Get portfolio settings
      const { data: settings, error: settingsError } = await supabase
        .from('portfolio_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();
        
      if (settingsError) throw settingsError;
      
      // Get active positions
      const { data: positions, error: positionsError } = await supabase
        .from('positions')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true);
        
      if (positionsError) throw positionsError;
      
      // Calculate current value of positions (this is simplified - would need current prices)
      let positionsValue = 0;
      // In a real implementation, we would fetch current prices for each position
      
      return {
        currentValue: settings.current_cash + positionsValue,
        initialValue: settings.initial_cash,
        cashBalance: settings.current_cash,
        totalReturn: (settings.current_cash + positionsValue) - settings.initial_cash,
        totalReturnPercentage: ((settings.current_cash + positionsValue) / settings.initial_cash - 1) * 100,
        activePositions: positions.length,
      };
    } catch (error) {
      console.error('Error getting portfolio summary:', error);
      return null;
    }
  },
}; 